import React, { useState } from 'react'
import { graphql } from 'relay-runtime'
import { useLazyLoadQuery } from 'react-relay'
import {
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  FormGroup,
  IconButton,
  Typography,
} from '@mui/material'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { FeatureFlagSelectorQuery } from './__generated__/FeatureFlagSelectorQuery.graphql'

const featureFlagsQuery = graphql`
  query FeatureFlagSelectorQuery {
    adminListFeatureFlags {
      id
      name
      enabled
    }
  }
`

type FlagScope = 'account' | 'brand' | 'user'

// Friendly, scope-specific explanation of what the flags do + the exclusion.
const SCOPE_NOTES: Record<FlagScope, string> = {
  account:
    'Applied to everyone in this account, across all brands.',
  brand:
    'Applied only when this brand is selected. Flags already on at the account level are hidden here.',
  user:
    'Applied only to this user. Flags already covered by their account or brands are hidden here.',
}

interface FeatureFlagSelectorProps {
  value: string[]
  onChange: (featureFlagIds: string[]) => void
  // Flag ids to hide because they're already assigned at a higher level
  // (account > brand > user). Hidden from both the catalog and the chips.
  excludeIds?: string[]
  // Which level this selector is for — drives the helper note.
  scope?: FlagScope
}

// Tokens that should stay fully uppercase rather than being title-cased.
const ACRONYMS = new Set([
  'CIH',
  'KPI',
  'ROAS',
  'CPM',
  'CTR',
  'LPV',
  'ROI',
  'CPA',
  'AI',
  'ID',
  'URL',
])

/** Turns a raw flag name like "DOMINANT_COLOR_LABEL_TYPE" into "Dominant Color Label Type" (keeping acronyms like "CIH"). */
const formatFlagName = (name: string) =>
  name
    .split('_')
    .map((word) =>
      ACRONYMS.has(word.toUpperCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ')

/**
 * Collapsible checkbox list of the feature-flag catalog, used in the
 * account/brand/user edit dialogs. Collapsed by default — it shows the
 * currently-assigned flags as chips; expand to add or remove flags.
 */
export const FeatureFlagSelector = ({
  value,
  onChange,
  excludeIds = [],
  scope,
}: FeatureFlagSelectorProps) => {
  const data = useLazyLoadQuery<FeatureFlagSelectorQuery>(
    featureFlagsQuery,
    {},
    { fetchPolicy: 'store-and-network' }
  )
  const flags = data.adminListFeatureFlags.filter(
    (flag) => !excludeIds.includes(flag.id)
  )
  const [expanded, setExpanded] = useState(false)

  const assignedFlags = flags.filter((flag) => value.includes(flag.id))

  const toggle = (id: string, checked: boolean) => {
    if (checked) {
      onChange([...value, id])
    } else {
      onChange(value.filter((flagId) => flagId !== id))
    }
  }

  return (
    <div>
      <div
        className="flex cursor-pointer items-center justify-between"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Typography variant="subtitle2" color="primary">
          Feature Flags
        </Typography>
        <IconButton size="small" aria-label="toggle feature flags">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </IconButton>
      </div>

      {scope && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.5 }}
        >
          {SCOPE_NOTES[scope]}
        </Typography>
      )}

      {!expanded && (
        <div className="mt-1 flex flex-wrap gap-1">
          {assignedFlags.length > 0 ? (
            assignedFlags.map((flag) => (
              <Chip
                key={flag.id}
                label={formatFlagName(flag.name)}
                size="small"
                color="primary"
              />
            ))
          ) : (
            <Typography variant="body2" color="text.disabled">
              No flags assigned
            </Typography>
          )}
        </div>
      )}

      <Collapse in={expanded}>
        {flags.length === 0 ? (
          <Typography variant="body2" color="text.disabled">
            No feature flags defined.
          </Typography>
        ) : (
          <FormGroup>
            {flags.map((flag) => (
              <FormControlLabel
                key={flag.id}
                control={
                  <Checkbox
                    size="small"
                    checked={value.includes(flag.id)}
                    onChange={(event) => toggle(flag.id, event.target.checked)}
                  />
                }
                label={
                  flag.enabled
                    ? formatFlagName(flag.name)
                    : `${formatFlagName(flag.name)} (globally off)`
                }
              />
            ))}
          </FormGroup>
        )}
      </Collapse>
    </div>
  )
}

export default FeatureFlagSelector
